import type { SearchClient } from 'instantsearch.js';
import { useMemo } from 'react';
import * as Typesense from 'typesense';
import TypesenseInstantsearchAdapter, { TypesenseInstantsearchAdapterOptions } from 'typesense-instantsearch-adapter';

import { trpc } from '../config/trpc';
import { useAuth } from '../context/AuthProvider';

export const useTypesense = (
  config?: Partial<TypesenseInstantsearchAdapterOptions>,
  isInstantSearchEnabled = false,
) => {
  const { isLoggedIn } = useAuth();
  const { data } = trpc.getTypesenseConfig.useQuery(undefined, {
    refetchOnWindowFocus: false,
    enabled: isLoggedIn,
  });

  const typesense = useMemo(() => {
    if (!data || isInstantSearchEnabled) return;

    return new TypesenseInstantsearchAdapter({
      server: {
        apiKey: data.key,
        nodes: [
          {
            host: data.host as string,
            port: Number(data?.port),
            protocol: data.protocol as string,
          },
        ],
      },
      ...config,
      additionalSearchParameters: {
        query_by: '*',
        per_page: 50,
        ...config?.additionalSearchParameters,
      },
    })?.searchClient;
  }, [config, data, isInstantSearchEnabled]);

  const typesenseClient = useMemo(() => {
    if (!data) return;

    return new Typesense.Client({
      nodes: [
        {
          host: data.host,
          port: Number(data.port),
          protocol: data.protocol,
        },
      ],
      connectionTimeoutSeconds: 60,
      retryIntervalSeconds: 30,
      apiKey: data.key,
    });
  }, [data]);

  return {
    searchClient: typesense as SearchClient,
    typesenseClient,
  };
};

export type TypesenseClient = Awaited<ReturnType<typeof useTypesense>>['typesenseClient'];
